// src/context/DeliveryFormContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "./AuthContext";
import { useUser } from "./UserContext";
import { useDeliveryDraft } from "../shared/hooks/useDeliveryDraft";
import { createDeliveryFn } from "../firebase/deliveryFunctions";

// ─── Helpers ────────────────────────────────────────────────────────────────

const toMillis = (ts) => {
  if (!ts) return null;
  if (typeof ts === "number") return ts;
  if (ts?.toDate) return ts.toDate().getTime();
  const ms = new Date(ts).getTime();
  return isNaN(ms) ? null : ms;
};

const DEFAULT_INITIAL = () => {
  const now = Date.now();
  return {
    // Location
    origin: "",
    destination: "",
    originData: null,
    destData: null,
    distance: null,
    duration: null,

    // Items
    items: [{ id: now, weight: 5, dimensions: { length: 30, width: 20, height: 15 }, sizeCategory: "small" }],
    selectedSize: "small",
    weightKg: 5,

    // Times — unix millis
    pickupTime: now + 30 * 60000,
    targetTime: now + 3 * 60 * 60000,
    hasTimeWindow: false,
    latestPickUpTime: now + 90 * 60000,
    earliestTargetTime: now + 150 * 60000,

    // Pickup details
    pickupApartment: "", pickupFloor: "", pickupBuilding: "",
    pickupAccessCode: "", pickupInstructions: "",

    // Sender
    senderName: "", senderPhone: "", senderEmail: "",

    // Org
    organizationId: null, organizationName: null,

    // Delivery details
    deliveryApartment: "", deliveryFloor: "", deliveryBuilding: "",
    deliveryAccessCode: "", deliveryInstructions: "",

    // Recipient
    recipientName: "", recipientPhone: "", recipientEmail: "",

    // Options
    returnParcels: false, returnMethod: "bus", temperatureControl: false, canBeLeftAtDoor: false,

    // Billing
    paymentMethod: "invoice", yNumber: "", companyName: "",
    paymentReference: "", billingDepartment: "",

    // Transport
    selectedOption: null, busTransportData: null,

    // Multi-stop
    actions: [],
  };
};

// ─── Context ─────────────────────────────────────────────────────────────────

const DeliveryFormContext = createContext(undefined);

export function DeliveryFormProvider({ children }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { userData } = useUser();
  const { loadDraft, saveDraft, clearDraft } = useDeliveryDraft(user?.uid);
  const draftRestored = useRef(false);

  // ── State ──────────────────────────────────────────────────────────────────
  const [deliveryData, setDeliveryData] = useState(() => {
    const base = DEFAULT_INITIAL();
    return base;
  });
  const [submitting, setSubmitting] = useState(false);

  const updateDelivery = useCallback((updates) => {
    setDeliveryData((prev) => ({ ...prev, ...updates }));
  }, []);

  // ── Seed sender info from userData once it loads ───────────────────────────
  useEffect(() => {
    if (!userData) return;

    updateDelivery({
      senderName: userData.firstName && userData.lastName
        ? `${userData.firstName} ${userData.lastName}` : "",
      senderPhone: userData.phone || "",
      senderEmail: userData.email || "",
      organizationId: userData.organizationId || null,
      organizationName: userData.organizationName || null,
      yNumber: userData?.billingInfo?.yNumber || "",
      companyName: userData?.billingInfo?.companyName || "",
      paymentReference: userData?.billingInfo?.reference || "",
    });
  }, [userData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Restore draft once on mount ────────────────────────────────────────────
  useEffect(() => {
    if (draftRestored.current) return;
    const draft = loadDraft();
    draftRestored.current = true;
    if (!draft) return;

    const now = Date.now();
    const timesArePast =
      !draft.pickupTime ||
      toMillis(draft.pickupTime) < now ||
      (draft.hasTimeWindow && toMillis(draft.latestPickUpTime) < now);

    if (timesArePast) {
      setDeliveryData({
        ...draft,
        pickupTime: now + 30 * 60000,
      });
    } else {
      setDeliveryData(draft);
    }
  }, [loadDraft]);

  // ── Auto-save draft on changes ─────────────────────────────────────────────
  useEffect(() => {
    if (!draftRestored.current) return;
    if (!deliveryData.origin && !deliveryData.destination &&
        !deliveryData.recipientName && !deliveryData.senderName) return;
    saveDraft(deliveryData);
  }, [deliveryData, saveDraft]);

  // ── Default weight by size ─────────────────────────────────────────────────
  useEffect(() => {
    const defaults = { small: 5, medium: 25, large: 50, xl: 100 };
    const def = defaults[deliveryData.selectedSize];
    if (def && !deliveryData.weightKg) updateDelivery({ weightKg: def });
  }, [deliveryData.selectedSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Distance matrix ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!deliveryData.originData || !deliveryData.destData || !window.google) return;
    const service = new window.google.maps.DistanceMatrixService();
    service.getDistanceMatrix(
      {
        origins: [{ lat: deliveryData.originData.latitude, lng: deliveryData.originData.longitude }],
        destinations: [{ lat: deliveryData.destData.latitude, lng: deliveryData.destData.longitude }],
        travelMode: "DRIVING",
        unitSystem: window.google.maps.UnitSystem.METRIC,
      },
      (response, status) => {
        if (status === "OK" && response.rows?.[0]?.elements?.[0]?.status === "OK") {
          const el = response.rows[0].elements[0];
          updateDelivery({ distance: el.distance.value, duration: el.duration.value });
        }
      }
    );
  }, [deliveryData.originData, deliveryData.destData, updateDelivery]);

  // ── Derived pricing ────────────────────────────────────────────────────────
  const estimatedPrice = useMemo(() =>
    deliveryData.selectedOption?.price != null
      ? Number(deliveryData.selectedOption.price).toFixed(2)
      : null,
    [deliveryData.selectedOption]
  );
  const timeWindowFee = deliveryData.hasTimeWindow ? 4 : 0;
  const totalPrice = useMemo(() =>
    estimatedPrice ? (Number(estimatedPrice) + timeWindowFee).toFixed(2) : null,
    [estimatedPrice, timeWindowFee]
  );

  const canSubmit =
    !!deliveryData.origin &&
    !!deliveryData.destination &&
    !!deliveryData.originData?.latitude &&
    !!deliveryData.destData?.latitude &&
    !!deliveryData.selectedOption?.optionId &&
    !submitting &&
    deliveryData.selectedOption?.price != null;

  const selectedTransportLabel = useMemo(() => {
    if (!deliveryData?.selectedOption?.optionId) return null;
    return deliveryData?.selectedOption?.option?.title || t("delivery.delivery");
  }, [deliveryData.selectedOption, t]);

  // ── Submit ─────────────────────────────────────────────────────────────────
  const confirmDelivery = useCallback(async (overrideData) => {
    // Merge overrideData on top of deliveryData so fields like `actions`
    // are always included regardless of what else overrideData contains.
    const data = overrideData?.deliveryTransportType
      ? { ...overrideData }
      : { ...deliveryData, ...(overrideData && typeof overrideData === "object" && !overrideData.nativeEvent ? overrideData : {}) };

    try {
      setSubmitting(true);

      const twFee = data.hasTimeWindow ? 4 : 0;
      const basePrice = overrideData?.estimatedPrice ?? estimatedPrice;
      const finalPrice = Number(basePrice) + twFee;
      const courierReward = (finalPrice * 0.7).toFixed(2);

      const isSpecialCourier = user?.roles && user?.roles.includes("specialCourier");
      const effectiveSenderId = isSpecialCourier ? data.createdForMemberId || user.uid : user.uid;

      const payload = {
        senderId: effectiveSenderId,
        origin: data.origin,
        originInfo: {
          address: data.origin,
          latitude: data.originData?.latitude,
          longitude: data.originData?.longitude,
          placeId: data.originData?.placeId,
          city: data.originData?.city || "",
          postalCode: data.originData?.postalCode || "",
          details: {
            apartment: data.pickupApartment || "",
            floor: data.pickupFloor || "",
            building: data.pickupBuilding || "",
            accessCode: data.pickupAccessCode || "",
            instructions: data.pickupInstructions || "",
          },
        },
        destination: data.destination,
        destinationInfo: {
          address: data.destination,
          latitude: data.destData?.latitude,
          longitude: data.destData?.longitude,
          placeId: data.destData?.placeId,
          city: data.destData?.city || "",
          postalCode: data.destData?.postalCode || "",
          details: {
            apartment: data.deliveryApartment || "",
            floor: data.deliveryFloor || "",
            building: data.deliveryBuilding || "",
            accessCode: data.deliveryAccessCode || "",
            instructions: data.deliveryInstructions || "",
          },
        },
        senderPhone: data.senderPhone || null,
        senderInfo: { name: data.senderName || null, email: data.senderEmail || null },
        receiverPhone: data.recipientPhone || null,
        receiverInfo: { name: data.recipientName || null, email: data.recipientEmail || null },
        organizationId: userData?.organizationId || null,
        organizationName: userData?.organizationName || null,
        shareInfo: data?.shareInfo || {},
        pickupTime: toMillis(data.pickupTime),
        targetTime: toMillis(data.targetTime),
        latestPickUpTime: data.hasTimeWindow ? toMillis(data.latestPickUpTime) : null,
        earliestTargetTime: data.hasTimeWindow ? toMillis(data.earliestTargetTime) : null,
        hasTimeWindow: data.hasTimeWindow,
        price: finalPrice,
        reward: Number(courierReward),
        deliveryType: data.deliveryType || "package",
        deliveryTransportType: data?.selectedOption?.optionId || data?.deliveryTransportType || "standard",
        distance: data.distance ? Math.round(data.distance) : null,
        duration: data.duration ? Math.round(data.duration) : null,
        packageSize: data.selectedSize,
        weightKg: Number(data.weightKg || 0),
        returnParcels: data.returnParcels || false,
        returnMethod: data.returnParcels ? (data.returnMethod || "bus") : null,
        temperatureControl: data.temperatureControl || false,
        canBeLeftAtDoor: data.canBeLeftAtDoor || false,
        billing: {
          paymentMethod: data.paymentMethod,
          yNumber: data.yNumber || null,
          companyName: data.companyName || null,
          paymentReference: data.paymentReference || null,
          billingDepartment: data.billingDepartment || null,
        },
        timeWindowFee: twFee,
        hide: false,
        ...((data?.selectedOption?.optionId === "eco-bus" || data?.deliveryTransportType === "eco-bus") && data.busTransportData && {
          busTransportData: data.busTransportData,
        }),
        ...(data?.selectedOption?.optionId === "eco-regular" && data.selectedOption?.regularData && {
          regularData: data.selectedOption.regularData,
        }),
        // Multi-stop actions
        ...(Array.isArray(data.actions) && data.actions.length > 0 && {
          actions: data.actions,
        }),
      };

      const { deliveryId } = await createDeliveryFn(payload);
      clearDraft();
      setDeliveryData(DEFAULT_INITIAL());
      navigate(`/laheta-paketti?tab=my-deliveries&deliveryId=${deliveryId}`);
      return { success: true, deliveryId };
    } catch (e) {
      console.error(e);
      return { success: false, error: e };
    } finally {
      setSubmitting(false);
    }
  }, [deliveryData, estimatedPrice, user, userData, clearDraft, navigate]);

  // ── Prefill from existing delivery (resend) ────────────────────────────────
  const prefillFromDelivery = useCallback((delivery) => {
    const now = Date.now();
    const fallbackPickup = now + 30 * 60000;
    const fallbackTarget = now + 3 * 60 * 60000;
    const fallbackLatest = now + 90 * 60000;
    const fallbackEarliest = now + 150 * 60000;

    const originInfo = delivery?.originInfo ?? delivery?.fromInfo ?? null;
    const destinationInfo = delivery?.destinationInfo ?? delivery?.toInfo ?? null;
    const originDetails = originInfo?.details || {};
    const destinationDetails = destinationInfo?.details || {};

    const originStr = delivery?.origin || originInfo?.address ||
      (typeof delivery?.from === "string" ? delivery.from : delivery?.from?.address) || "";
    const destStr = delivery?.destination || destinationInfo?.address ||
      (typeof delivery?.to === "string" ? delivery.to : delivery?.to?.address) || "";

    const pickupTimeRaw = delivery?.pickupTime ?? delivery?.pickUpTime;
    const latestPickUpTimeRaw = delivery?.latestPickUpTime ?? delivery?.latestPickupTime;

    updateDelivery({
      origin: originStr,
      destination: destStr,
      pickupTime: toMillis(pickupTimeRaw) ?? fallbackPickup,
      targetTime: toMillis(delivery?.targetTime) ?? fallbackTarget,
      hasTimeWindow: !!delivery?.hasTimeWindow,
      latestPickUpTime: toMillis(latestPickUpTimeRaw) ?? fallbackLatest,
      earliestTargetTime: toMillis(delivery?.earliestTargetTime) ?? fallbackEarliest,
      originData: originInfo ? {
        latitude: originInfo.latitude, longitude: originInfo.longitude,
        address: originInfo.address, placeId: originInfo.placeId,
        city: originInfo.city, postalCode: originInfo.postalCode,
      } : null,
      destData: destinationInfo ? {
        latitude: destinationInfo.latitude, longitude: destinationInfo.longitude,
        address: destinationInfo.address, placeId: destinationInfo.placeId,
        city: destinationInfo.city, postalCode: destinationInfo.postalCode,
      } : null,
      selectedSize: delivery?.packageSize || deliveryData.selectedSize,
      weightKg: typeof delivery?.weightKg === "number" ? delivery.weightKg : deliveryData.weightKg,
      pickupApartment: originDetails.apartment || "",
      pickupFloor: originDetails.floor || "",
      pickupBuilding: originDetails.building || "",
      pickupAccessCode: originDetails.accessCode || "",
      pickupInstructions: originDetails.instructions || "",
      senderName: delivery?.senderInfo?.name || "",
      senderPhone: delivery?.senderPhone || "",
      senderEmail: delivery?.senderInfo?.email || "",
      deliveryApartment: destinationDetails.apartment || "",
      deliveryFloor: destinationDetails.floor || "",
      deliveryBuilding: destinationDetails.building || "",
      deliveryAccessCode: destinationDetails.accessCode || "",
      deliveryInstructions: destinationDetails.instructions || "",
      recipientName: delivery?.receiverInfo?.name || "",
      recipientPhone: delivery?.receiverPhone || "",
      recipientEmail: delivery?.receiverInfo?.email || "",
      returnParcels: !!delivery?.returnParcels,
      temperatureControl: !!delivery?.temperatureControl,
      canBeLeftAtDoor: !!delivery?.canBeLeftAtDoor,
      paymentMethod: delivery?.billing?.paymentMethod || "invoice",
      yNumber: delivery?.billing?.yNumber || "",
      companyName: delivery?.billing?.companyName || "",
      paymentReference: delivery?.billing?.paymentReference || "",
      billingDepartment: delivery?.billing?.billingDepartment || "",
      distance: typeof delivery?.distance === "number" ? delivery.distance : deliveryData.distance,
      duration: typeof delivery?.duration === "number" ? delivery.duration : deliveryData.duration,
      // Don't prefill actions — resend starts fresh
      actions: [],
    });
  }, [updateDelivery, deliveryData.selectedSize, deliveryData.weightKg, deliveryData.distance, deliveryData.duration]);

  const resetForm = useCallback(() => {
    clearDraft();
    setDeliveryData(DEFAULT_INITIAL());
  }, [clearDraft]);

  const value = {
    deliveryData,
    updateDelivery,
    submitting,
    estimatedPrice,
    timeWindowFee,
    totalPrice,
    canSubmit,
    selectedTransportLabel,
    confirmDelivery,
    prefillFromDelivery,
    clearDraft,
    resetForm,
  };

  return (
    <DeliveryFormContext.Provider value={value}>
      {children}
    </DeliveryFormContext.Provider>
  );
}

export function useDeliveryForm() {
  const ctx = useContext(DeliveryFormContext);
  if (!ctx) throw new Error("useDeliveryForm must be used within DeliveryFormProvider");
  return ctx;
}